// A menu anchored to an arbitrary trigger element, rendered into a body-level portal.
//
// Why a portal rather than an absolutely-positioned child (the RichSelect `fixedOptions` idiom):
// inside the bulk grid three separate mechanisms would defeat an in-tree panel — `.panel-scroll` is
// `overflow: hidden` with `> * { position: absolute; inset: 0; overflow: auto }` so it both CLIPS an
// absolute child and would force a sibling full-bleed; a sticky `th` at `z-index: 2` creates a
// stacking context that traps even a `position: fixed` descendant; and `th.frozen` at `z-index: 3`
// paints over anything that overhangs it. A body-level fixed layer escapes all three.
//
// Unlike `fixedOptions`, this repositions on SCROLL as well as resize — the grid scrolls in both axes
// under a sticky header, and a panel measured once would strand itself mid-air.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { css } from '/imports/ui/css';

// Below Modal's 100, so ColumnPicker and the import preview still cover this.
const PopoverStyled = styled.div`
  position: fixed;
  z-index: 90;
  background: ${css.white};
  border: 1px solid ${css.grey6};
  border-radius: 0.4rem;
  box-shadow: 0 0.4rem 1.2rem rgba(0, 0, 0, 0.18);
  overflow: auto;
  font-size: 0.85rem;
`;

const MARGIN = 8;
const MIN_HEIGHT = 160;

export interface PopoverProps {
  anchor?: DOMRect;                 // the trigger's viewport rect, re-measured by the owner on scroll
  /** The element that opened this. Excluded from the outside-click test so the trigger can also CLOSE
   *  the panel: without it, mousedown closes and the following click re-opens, and the panel sticks. */
  trigger?: HTMLElement | null;
  width?: number;
  className?: string;
  onClose(): void;
  children?: React.ReactNode;
}

/**
 * Positions a floating panel under `anchor`, flipping left when it would overflow the right edge and
 * upward when there is more room above. Closes on Escape or a click outside.
 */
export const Popover = (props: PopoverProps) => {
  const { anchor, trigger, width = 260, onClose } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!anchor) return;
    const below = window.innerHeight - anchor.bottom - MARGIN;
    const above = anchor.top - MARGIN;
    const flipUp = below < MIN_HEIGHT && above > below;
    setStyle({
      width,
      left: Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - width - MARGIN)),
      ...(flipUp ? { bottom: window.innerHeight - anchor.top + 2 } : { top: anchor.bottom + 2 }),
      maxHeight: Math.max(MIN_HEIGHT, (flipUp ? above : below) - 2),
    });
  }, [anchor?.top, anchor?.left, anchor?.bottom, anchor?.width, width]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const el = ref.current;
      const node = e.target as Node;
      // The trigger handles its own toggle; only treat clicks fully outside BOTH as a dismiss.
      if (el && !el.contains(node) && !trigger?.contains(node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // Capture phase, so a click that unmounts its own target still registers as outside.
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [onClose, trigger]);

  if (!anchor) return null;
  return createPortal(
    <PopoverStyled ref={ref} className={`popover ${props.className || ''}`} style={style} onMouseDown={(e) => e.stopPropagation()}>
      {props.children}
    </PopoverStyled>,
    document.body,
  );
};

/**
 * Keeps a trigger's viewport rect current while a popover is open: any scroll (capture phase, so
 * inner scrollers count) or resize re-measures it.
 * @param el - the trigger element, or undefined when nothing is open
 */
export const useAnchorRect = (el?: HTMLElement | null): DOMRect | undefined => {
  const [rect, setRect] = useState<DOMRect>();
  useEffect(() => {
    if (!el) { setRect(undefined); return undefined; }
    const measure = () => setRect(el.getBoundingClientRect());
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    // Neither event fires when the anchor itself is resized — dragging a grid column, for one — so
    // observe the element too, or the panel strands at its old coordinates.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    ro?.observe(el);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [el]);
  return rect;
};
